import { Home, ArrowRight, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { SectionTitle, GoldBadge } from '../Luxury';
import type { RentValidationResult, RentDecision } from '../../types/calculations';

interface RentSource {
  name: string;
  value: number;
  source?: string;
  selected?: boolean;
  confidence?: string;
}

interface RentAnalysisConsolidatedProps {
  currentRentPerUnit: number;
  marketRentPerUnit: number;
  rentSources?: RentSource[];
  rentUpliftPercent: number;
  annualRentUplift?: number;
  totalUnits: number;
  capexBudget: number;
  rentValidation?: RentValidationResult;
  currentRentSource?: string;
  marketRentSource?: string;
  currentRentConfidence?: string;
  rentDecision?: RentDecision;
}

// Helper to extract rent value from RentValidationResult rent fields
const getRentValue = (rent: number | { value?: number } | undefined): number => {
  if (typeof rent === 'number') return rent;
  return rent?.value ?? 0;
};

const getConfidenceLevel = (rent: number | { confidence_level?: string } | undefined): string | undefined => {
  if (typeof rent === 'number' || !rent) return undefined;
  return (rent as any).confidence_level;
};

const getRentSource = (rent: number | { source?: string } | undefined): string => {
  if (typeof rent === 'number' || !rent) return 'Estimate';
  return (rent as any).source ?? 'Estimate';
};

function ConfidenceIndicator({ level }: { level?: string }) {
  if (!level) return null;
  const color = level === 'high' || level === 'very_high'
    ? 'text-emerald bg-emerald/15 border-emerald/30'
    : level === 'medium'
      ? 'text-amber-400 bg-amber-500/15 border-amber-500/30'
      : 'text-crimson bg-crimson/15 border-crimson/30';

  return (
    <span className={`text-sm uppercase tracking-wider px-2 py-0.5 rounded-full border ${color}`}>
      {level.replace('_', ' ')}
    </span>
  );
}

/** Confidence badge colors for the comparison table */
function ConfidenceBadge({ level }: { level: string }) {
  const normalized = level.toLowerCase();
  const color = normalized === 'high'
    ? 'text-emerald bg-emerald/15 border-emerald/30'
    : normalized === 'medium'
      ? 'text-amber-400 bg-amber-500/15 border-amber-500/30'
      : normalized === 'low'
        ? 'text-crimson bg-crimson/15 border-crimson/30'
        : 'text-stone-400 bg-stone-500/10 border-stone-600/30';

  return (
    <span className={`text-sm uppercase tracking-wider px-1.5 py-0.5 rounded border font-medium ${color}`}>
      {normalized}
    </span>
  );
}


export function RentAnalysisConsolidated({
  currentRentPerUnit,
  marketRentPerUnit,
  rentSources,
  rentUpliftPercent,
  annualRentUplift,
  totalUnits,
  capexBudget,
  rentValidation,
  currentRentSource,
  marketRentSource,
  currentRentConfidence,
  rentDecision,
}: RentAnalysisConsolidatedProps) {
  const monthlyDelta = marketRentPerUnit - currentRentPerUnit;
  const computedAnnualUplift = annualRentUplift ?? (monthlyDelta * 12 * totalUnits);

  // Merge validation data with transparency data for best display
  const validationCurrentRent = rentValidation ? getRentValue(rentValidation.current_rent) : 0;
  const validationMarketRent = rentValidation ? getRentValue(rentValidation.market_rent) : 0;
  const validationCurrentConf = rentValidation ? getConfidenceLevel(rentValidation.current_rent) : undefined;
  const validationCurrentSrc = rentValidation ? getRentSource(rentValidation.current_rent) : undefined;
  const validationMarketSrc = rentValidation ? getRentSource(rentValidation.market_rent) : undefined;

  const displayCurrentRent = currentRentPerUnit || validationCurrentRent;
  const displayMarketRent = marketRentPerUnit || validationMarketRent;
  const displayCurrentConf = currentRentConfidence || validationCurrentConf;
  const displayCurrentSrc = currentRentSource || validationCurrentSrc || 'Estimate';
  const displayMarketSrc = marketRentSource || validationMarketSrc || 'Zandi/RentCast';

  // Sort rent decision sources by value descending
  const sortedSources = rentDecision?.sources
    ? [...rentDecision.sources].sort((a, b) => b.value - a.value)
    : [];

  return (
    <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
      <SectionTitle icon={<Home className="w-5 h-5" />}>
        Rent Analysis
      </SectionTitle>

      {/* Rent Data Sources — every API source shown clearly */}
      {rentDecision && sortedSources.length > 0 && (
        <div className="mt-5 mb-6">
          <h4 className="text-sm uppercase tracking-[0.12em] text-stone-400 font-semibold mb-3">
            All Rent Data Sources
          </h4>
          <div className="space-y-2">
            {sortedSources.map((src) => {
              const role = src.selected_for;
              const isCurrentRent = role === 'current_rent';
              const isMarketRent = role === 'market_rent';
              const borderColor = isCurrentRent
                ? 'border-gold'
                : isMarketRent
                  ? 'border-emerald'
                  : 'border-slate-dark';
              const bgColor = isCurrentRent
                ? 'bg-gold/5'
                : isMarketRent
                  ? 'bg-emerald/5'
                  : 'bg-obsidian/50';

              return (
                <div
                  key={src.name}
                  className={`flex items-center justify-between p-4 rounded-lg border ${borderColor} ${bgColor}`}
                >
                  {/* Left: Source name + detail */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-ivory">
                        {src.name}
                      </span>
                      <ConfidenceBadge level={src.confidence} />
                    </div>
                    {src.note && (
                      <p className="text-sm text-stone-400 mt-1">{src.note}</p>
                    )}
                  </div>

                  {/* Right: Value + role */}
                  <div className="flex items-center gap-4 shrink-0 ml-4">
                    <span className={`font-mono tabular-nums text-[18px] font-bold ${
                      isCurrentRent ? 'text-gold' : isMarketRent ? 'text-emerald' : 'text-ivory'
                    }`}>
                      {src.value > 0 ? `${formatCurrency(src.value)}` : 'N/A'}
                      <span className="text-sm font-normal text-stone-400">/mo</span>
                    </span>
                    {role ? (
                      <span className={`text-sm uppercase tracking-wider px-2.5 py-1 rounded border font-bold whitespace-nowrap ${
                        isCurrentRent
                          ? 'text-gold bg-gold/15 border-gold/30'
                          : 'text-emerald bg-emerald/15 border-emerald/30'
                      }`}>
                        {isCurrentRent ? '← CURRENT' : '← MARKET'}
                      </span>
                    ) : (
                      <span className="text-sm text-stone-400 w-[70px] text-center">reference</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Divergence warnings */}
          {rentDecision.divergence_flags && rentDecision.divergence_flags.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {rentDecision.divergence_flags.map((flag, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-amber-400">{flag}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rent Sources — legacy grid display (fallback when no rent_decision) */}
      {!rentDecision && rentSources && rentSources.length > 0 && (
        <div className="mt-5 mb-6">
          <h4 className="text-sm uppercase tracking-[0.12em] text-stone-400 font-semibold mb-3">
            Data Sources
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {rentSources.map((src) => (
              <div
                key={src.name}
                className={`p-3 rounded-lg border ${
                  src.selected
                    ? 'border-gold bg-gold/5'
                    : 'border-slate-dark bg-obsidian/50'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-stone-400 uppercase tracking-wider">
                    {src.name}
                  </span>
                  {src.selected && (
                    <GoldBadge variant="gold">Selected</GoldBadge>
                  )}
                </div>
                <div className="text-lg font-mono tabular-nums font-semibold text-ivory">
                  {src.value > 0 ? `${formatCurrency(src.value)}/mo` : 'N/A'}
                </div>
                {src.source && (
                  <div className="text-sm text-stone-400 mt-1">{src.source}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current -> Market Rent Flow */}
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 mb-6">
        {/* Current Rent */}
        <div className="flex-1 w-full border-2 border-gold bg-gradient-to-br from-gold/10 to-gold/5 p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-sm uppercase tracking-[0.15em] text-stone-400 font-semibold">
              Current Rent / Unit
            </span>
            <ConfidenceIndicator level={displayCurrentConf} />
          </div>
          <div className="text-[28px] font-bold font-mono tabular-nums text-gold">
            {formatCurrency(displayCurrentRent)}<span className="text-sm text-gold/80">/mo</span>
          </div>
          <div className="text-sm text-stone-400 mt-1">Source: {displayCurrentSrc}</div>
        </div>

        {/* Arrow with uplift badge */}
        <div className="flex flex-col items-center gap-2">
          <GoldBadge variant="gold">+{rentUpliftPercent.toFixed(1)}%</GoldBadge>
          <div className="hidden sm:block">
            <div className="w-16 h-0.5 bg-gradient-to-r from-gold/40 via-gold to-emerald/40" />
          </div>
          <ArrowRight className="w-5 h-5 text-emerald hidden sm:block" />
        </div>

        {/* Market Rent */}
        <div className="flex-1 w-full border-2 border-emerald bg-gradient-to-br from-emerald/10 to-emerald/5 p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-sm uppercase tracking-[0.15em] text-stone-400 font-semibold">
              Market Rent / Unit
            </span>
          </div>
          <div className="text-[28px] font-bold font-mono tabular-nums text-emerald">
            {formatCurrency(displayMarketRent)}<span className="text-sm text-emerald/80">/mo</span>
          </div>
          <div className="text-sm text-stone-400 mt-1">Source: {displayMarketSrc}</div>
        </div>
      </div>

      {/* Uplift Summary — gross rent → NOI conversion → CapEx sizing */}
      <div className="p-4 bg-obsidian border border-slate-dark rounded-lg mb-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-sm uppercase tracking-[0.1em] text-stone-400 mb-1">Monthly Delta</div>
            <div className="text-lg font-mono tabular-nums font-bold text-emerald">
              +{formatCurrency(monthlyDelta)}/mo
            </div>
            <div className="text-sm text-stone-500 mt-0.5">per unit</div>
          </div>
          <div>
            <div className="text-sm uppercase tracking-[0.1em] text-stone-400 mb-1">Gross Rent Uplift</div>
            <div className="text-lg font-mono tabular-nums font-bold text-gold">
              +{formatCurrency(computedAnnualUplift)}/yr
            </div>
            <div className="text-sm text-stone-500 mt-0.5">{totalUnits} units × ${Math.round(monthlyDelta)}/mo × 12</div>
          </div>
          <div>
            <div className="text-sm uppercase tracking-[0.1em] text-stone-400 mb-1">NOI Uplift</div>
            <div className="text-lg font-mono tabular-nums font-bold text-gold">
              +{formatCurrency(Math.round(computedAnnualUplift * (1 - 0.07) * (1 - 0.425)))}/yr
            </div>
            <div className="text-sm text-stone-500 mt-0.5">after 7% vacancy + 42.5% OpEx</div>
          </div>
          <div>
            <div className="text-sm uppercase tracking-[0.1em] text-stone-400 mb-1">CapEx Budget</div>
            <div className="text-lg font-mono tabular-nums font-bold text-ivory">
              {formatCurrency(capexBudget)}
            </div>
            <div className="text-sm text-stone-500 mt-0.5">SSOT default ($28K/unit)</div>
          </div>
        </div>
      </div>

      {/* Validation warnings */}
      {rentValidation?.warnings && rentValidation.warnings.length > 0 && (
        <div className="space-y-1 mt-3">
          {rentValidation.warnings.map((w, i) => (
            <p key={i} className="text-sm text-amber-400 flex items-center gap-1.5">
              <span className="text-amber-500">!</span> {w}
            </p>
          ))}
        </div>
      )}

      {rentValidation?.is_circular_logic && (
        <div className="mt-3 p-2 bg-crimson/10 border border-crimson/30 rounded text-sm text-crimson">
          Circular logic detected — validate rent estimates manually
        </div>
      )}
    </div>
  );
}
